import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";
import { Share2, Copy, Gift, Sparkles, ChevronRight } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const ReferEarn = () => {
    const [me, setMe] = useState(null);
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get("/referrals/me").then((r) => setMe(r.data)),
            api.get("/referrals").then((r) => setList(r.data.items || [])),
        ]).finally(() => setLoading(false));
    }, []);

    const share = async () => {
        if (!me) return;
        if (navigator.share) {
            try { await navigator.share({ title: "Join me on PILLS", text: me.share_text, url: me.share_url }); return; }
            catch { /* fall through */ }
        }
        copy();
    };
    const copy = () => {
        navigator.clipboard.writeText(me?.share_text || "");
        toast.success("Share text copied to clipboard");
    };

    return (
        <AuthGuard>
            <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
                <Navbar />
                <main className="mx-auto max-w-3xl px-6 py-10 lg:px-12">
                    <p className="label-uppercase text-zinc-500">Refer &amp; Earn</p>
                    <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50" style={{ letterSpacing: "-0.01em" }}>
                        Give ₹{me?.reward_referee ?? 100}, get ₹{me?.reward_referrer ?? 150}
                    </h1>
                    <p className="mt-1 text-sm text-zinc-500">
                        Friends get ₹{me?.reward_referee ?? 100} wallet credit on signup. You get ₹{me?.reward_referrer ?? 150} when their first order is at least ₹{me?.qualify_min_order ?? 300}.
                    </p>

                    {/* Code card */}
                    <div className="mt-8 overflow-hidden rounded-3xl border border-zinc-100 bg-gradient-to-br from-brand-50 via-white to-violet-50 shadow-sm dark:from-brand-950/30 dark:via-zinc-900 dark:to-violet-950/30 dark:border-white/10">
                        <div className="p-7">
                            <p className="text-xs font-medium uppercase tracking-wider text-brand-700/80">Your referral code</p>
                            <p className="mt-2 font-mono text-3xl font-semibold tracking-widest text-zinc-900 dark:text-zinc-50" data-testid="referral-code">{loading ? "…" : me?.code}</p>
                            <p className="mt-3 text-sm text-zinc-600 dark:text-zinc-300">{me?.share_text}</p>
                            <div className="mt-5 flex flex-wrap gap-2">
                                <button onClick={share} className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800" data-testid="referral-share-btn">
                                    <Share2 className="h-4 w-4" /> Share
                                </button>
                                <button onClick={copy} className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-50 dark:bg-white/5 dark:text-zinc-50" data-testid="referral-copy-btn">
                                    <Copy className="h-4 w-4" /> Copy text
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="mt-6 grid grid-cols-3 gap-3" data-testid="referral-stats">
                        <Stat label="Invited" value={me?.invited ?? 0} />
                        <Stat label="Qualified" value={me?.qualified ?? 0} icon={<Sparkles className="h-3.5 w-3.5" />} />
                        <Stat label="Earned" value={inr(me?.total_earned ?? 0)} icon={<Gift className="h-3.5 w-3.5" />} />
                    </div>

                    {/* List */}
                    <section className="mt-8">
                        <p className="label-uppercase text-zinc-500">Your referrals</p>
                        {list.length === 0 ? (
                            <p className="mt-3 text-sm text-zinc-500">No referrals yet. Share your code and start earning.</p>
                        ) : (
                            <ul className="mt-3 space-y-2" data-testid="referrals-list">
                                {list.map((r) => (
                                    <li key={r.id} className="flex items-center justify-between gap-3 rounded-2xl border border-zinc-100 bg-white p-4 dark:bg-white/[0.03]">
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-50">{r.referee_name || r.referee_email_masked}</p>
                                            <p className="text-xs text-zinc-500">Joined {new Date(r.created_at).toLocaleDateString()}</p>
                                        </div>
                                        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${r.status === "qualified" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-700"}`}>
                                            {r.status === "qualified" ? "Qualified" : "Pending first order"}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </main>
                <Footer />
            </div>
        </AuthGuard>
    );
};

const Stat = ({ label, value, icon }) => (
    <div className="rounded-2xl border border-zinc-100 bg-white p-4 dark:bg-white/[0.03]">
        <p className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-zinc-500">{icon}{label}</p>
        <p className="mt-1 text-xl font-semibold text-zinc-900 dark:text-zinc-50">{value}</p>
    </div>
);

export default ReferEarn;
